// Shape returned by `GET /api/me` — the authenticated user's profile
// snapshot. Source of truth for both the API handler and the client
// ProfilePage.
//
// The "me" payload spans two sources:
//   1. Account fields from `users` (id, email, name, phone, verification flags).
//   2. Wizard-collected fields from the user's linked `job_request` row
//      (jobs, address, experience_level, citizenship, bio, requested
//      documents) — present once OTP signup links the anonymous submission
//      to the new account, null for users created via the Google IdP
//      bootstrap path who never went through the wizard.
//   3. The user's `user_documents` rows with their files, so the page can
//      render upload statuses (unverified/pending_review/approved/rejected).
//
// Field names stay snake_case to match the existing JSON contract
// (see apps/api/src/routes/auth.ts).
import { z } from 'zod';
// Account fields validated strictly. `job_request` and `documents` are
// validated permissively (z.unknown) because their canonical shapes already
// live in JobRequest / UserDocument; we recover the strong types via the
// TypeScript intersection below.
// Account-preference locales — the full client language set (mirrors
// EMAIL_LOCALES in email.ts; migration 031 extends the users CHECK).
export const SUPPORTED_LOCALES = [
    'en', 'ru', 'ar', 'de', 'tr', 'es', 'fr', 'pt', 'hi', 'pa',
    'sr', 'hr', 'bs', 'sl', 'mk', 'sq', 'hu',
];
export const meProfileSchema = z.object({
    user_id: z.string().uuid(),
    email: z.string().email(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    phone_e164: z.string().nullable(),
    phone_country: z.string().nullable(),
    email_verified: z.boolean(),
    phone_verified: z.boolean(),
    has_password: z.boolean(),
    // How the account was created originally — drives UI affordances like
    // whether the "Set a password" CTA is relevant (only for 'otp' users).
    signup_method: z.enum(['otp', 'idp']),
    preferred_locale: z.enum(SUPPORTED_LOCALES).nullable(),
    // Short-lived presigned GET URL for the avatar object, or null if the
    // user has not uploaded one. The URL expires — clients should not cache
    // it past a few minutes; refetch /api/me to get a fresh one.
    avatar_url: z.string().url().nullable(),
    roles: z.array(z.string()),
    created_at: z.string(),
    updated_at: z.string(),
    job_request: z.unknown().nullable(),
    documents: z.array(z.unknown()),
});
//# sourceMappingURL=me.js.map